<template>
  <div class="ui-doclist">
    <div class="ui-doclist__prompt-row">
      <div class="ui-doclist__prompt">{{ block.props.prompt }}</div>
      <!-- Engine-flagged (duplicate-create gate): standard side-by-side
           compare over the listed documents. In-app only. -->
      <button
        v-if="canCompare"
        type="button"
        class="ui-doclist__compare"
        :title="`Compare ${comparableDocs.length} documents side-by-side`"
        @click="compareDocs"
      >
        <v-icon size="12" color="indigo darken-1">mdi-compare</v-icon>
        Compare
      </button>
    </div>
    <template v-if="interactive">
      <div v-if="!block.props.items.length" class="ui-doclist__empty">
        No documents to show.
      </div>
      <!-- Result rows (DS MatchItem anatomy): title + other-hierarchy
           context on the top line, status dot on the second. The RM
           reference is deliberately not shown — actions carry it. The
           main hierarchy lives in the family group headings. -->
      <div v-else class="ui-doclist__results">
        <template v-for="row in displayRows">
        <div
          v-if="row.type === 'header'"
          :key="`family-${row.family}`"
          class="ui-group-label"
        >{{ row.family }}<span
          v-if="row.description"
          class="ui-group-label__desc"
        > — {{ row.description }}</span></div>
        <div
          v-else
          :key="row.doc.reference"
          class="ui-doclist__result"
          role="button"
          tabindex="0"
          @click="itemClick(row.doc, $event)"
          @keyup.enter.self="itemClick(row.doc, $event)"
        >
          <div class="ui-doclist__result-text">
            <span class="ui-doclist__name">
              <span class="ui-doclist__name-text">{{ row.doc.name }}</span>
              <span
                v-if="row.doc.other_hierarchies && row.doc.other_hierarchies.length"
                class="ui-doclist__ctx"
              >{{ row.doc.other_hierarchies.join(' · ') }}</span>
            </span>
            <!-- Second line: status dot + the row's quiet actions
                 (Open, and View for the quick-view popover). -->
            <span
              v-if="row.doc.status || canOpenInApp(row.doc) || row.doc.portal_url || canQuickView(row.doc)"
              class="ui-doclist__sub"
            >
              <template v-if="row.doc.status">
                <span
                  class="ui-doclist__dot"
                  :style="{ background: statusColor(row.doc.status) }"
                  aria-hidden="true"
                />
                <span>{{ row.doc.status }}</span>
              </template>
              <button
                v-if="canOpenInApp(row.doc)"
                type="button"
                class="ui-doclist__open"
                :title="`${openLabel} ${row.doc.reference}`"
                @click.stop="openDoc(row.doc)"
                @keyup.enter.stop
              >{{ openLabel }}</button>
              <a
                v-else-if="row.doc.portal_url"
                :href="row.doc.portal_url"
                target="_blank"
                rel="noopener"
                class="ui-doclist__open"
                :title="`${openLabel} ${row.doc.reference} in RoleMapper`"
                @click.stop
              >{{ openLabel }}</a>
              <button
                v-if="canQuickView(row.doc)"
                type="button"
                class="ui-doclist__view"
                :title="`Quick view ${row.doc.reference}`"
                @click.stop="quickView($event, row.doc)"
                @keyup.enter.stop
              >
                <v-icon size="13" class="ui-doclist__view-icon">mdi-eye-outline</v-icon>
                View
              </button>
            </span>
            <ul
              v-if="row.doc.match_reasons && row.doc.match_reasons.length && isWhyOpen(row.doc)"
              class="ui-why-list"
            >
              <li
                v-for="(reason, ri) in row.doc.match_reasons"
                :key="ri"
                class="ui-why-item"
              >
                <v-icon size="13" class="ui-why-check">mdi-check</v-icon>
                <span>{{ reason }}</span>
              </li>
            </ul>
          </div>
          <!-- Trailing column: match pill with the "Why it matches"
               toggle beneath it. The toggle never fires the row. -->
          <span
            v-if="row.doc.match || (row.doc.match_reasons && row.doc.match_reasons.length)"
            class="ui-doclist__trail"
          >
            <span
              v-if="row.doc.match"
              class="ui-match"
              :class="`ui-match--${row.doc.match}`"
              :title="matchTitle(row.doc.match)"
            >{{ matchLabel(row.doc.match) }}</span>
            <button
              v-if="row.doc.match_reasons && row.doc.match_reasons.length"
              type="button"
              class="ui-why-toggle"
              :class="{ 'ui-why-toggle--open': isWhyOpen(row.doc) }"
              :aria-expanded="isWhyOpen(row.doc) ? 'true' : 'false'"
              @click.stop="toggleWhy(row.doc)"
              @keyup.enter.stop
            >
              <v-icon size="13">mdi-help-circle-outline</v-icon>
              Why it matches
            </button>
          </span>
        </div>
        </template>
      </div>
      <!-- Engine-emitted placement picker (create gate, NEW title):
           choose the family/group the new title sits under. The pick
           rides the "Create anyway" action as `_node_parent`; the
           notice spells out the consequence of skipping it (unplaced
           profile, no generated content). -->
      <div
        v-if="placementField || (interactive && block.props.placement_notice)"
        class="ui-doclist__placement"
      >
        <label
          v-if="placementField"
          class="ui-field-label"
          :for="`uidl-${block.block_id}-placement`"
        >{{ placementField.label }}</label>
        <!-- Each guarded on placementField: the wrapper also renders for
             a notice with no picker (the title already has a home). -->
        <UiBlockTreePicker
          v-if="placementField && placementField.type === 'tree'"
          :tree="placementField.tree"
          @pick="placementPick = $event"
          @clear="placementPick = null"
        />
        <select
          v-else-if="placementField && placementField.type === 'select'"
          :id="`uidl-${block.block_id}-placement`"
          v-model="placementValue"
          class="ui-field-input ui-field-select"
        >
          <option value="">— none —</option>
          <option v-for="opt in placementField.options" :key="opt" :value="opt">{{ opt }}</option>
        </select>
        <input
          v-else-if="placementField"
          :id="`uidl-${block.block_id}-placement`"
          v-model="placementValue"
          type="text"
          class="ui-field-input"
          :placeholder="placementField.placeholder || ''"
        />
        <!-- With a picker this is the consequence of skipping it; on its
             own it explains why no picker was needed (the title already
             has a home) — informational, so a calmer tone. -->
        <div
          v-if="block.props.placement_notice"
          class="ui-doclist__placement-notice"
          :class="{ 'ui-doclist__placement-notice--info': !placementField }"
        >
          {{ block.props.placement_notice }}
        </div>
      </div>
      <!-- Engine-emitted footer actions (e.g. the duplicate-create
           gate's "Create anyway" + subtle "Exit workflow") — both ride the
           workflow-action channel. -->
      <div
        v-if="block.props.footer_action || block.props.secondary_action"
        class="ui-doclist__footer"
      >
        <button
          v-if="block.props.footer_action"
          type="button"
          class="ui-doclist__footer-action"
          @click="actionClick(block.props.footer_action, true)"
        >
          {{ block.props.footer_action.label }}
        </button>
        <button
          v-if="block.props.secondary_action"
          type="button"
          class="ui-doclist__secondary-action"
          @click="actionClick(block.props.secondary_action)"
        >
          {{ block.props.secondary_action.label }}
        </button>
      </div>
    </template>
    <div v-else class="ui-doclist__inert">
      <template v-if="chosenLabel">Selected: {{ chosenLabel }}</template>
      <template v-else>This step is no longer active.</template>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';

import { computed, ref } from 'vue';
import {
  docStatusColor,
  groupDocRowsByFamily,
  matchTierLabel,
  matchTierTitle,
} from '@rm-ui/agent/uiBlockSpec';
import UiBlockTreePicker from './UiBlockTreePicker.vue';

/**
 * `doc_list` ui_block — a displayed list of documents (engine-resolved;
 * view permission already enforced).
 *
 * Portal item actions:
 *  - clicking an ITEM opens the existing document-summary popup when
 *    the host page supports it (`summaryAvailable`); otherwise it
 *    advances the workflow (`selected`) as MCP hosts do;
 *  - each item also carries an "Open" action (label configurable via
 *    `open_label`): when the host page registered `document.open`
 *    (`openAvailable`) it opens the standard in-app document view;
 *    otherwise it falls back to the viewdoc deep link in a new tab.
 * Advancing conversationally ("use RM-x") always remains available via
 * the model.
 */
export default defineComponent({
  name: 'UiBlockDocList',
  components: { UiBlockTreePicker },
  props: {
    block: { type: Object, required: true },
    interactive: { type: Boolean, default: false },
    chosenLabel: { type: String, default: null },
    /** True when the host page registered the document.summary action. */
    summaryAvailable: { type: Boolean, default: false },
    /** True when the host page registered the document.open action. */
    openAvailable: { type: Boolean, default: false },
    /** True when the host page can run the standard document compare. */
    compareAvailable: { type: Boolean, default: false },
  },
  emits: ['action', 'open-summary', 'open-document', 'compare-documents'],
  setup(props, { emit }) {
    const openLabel = computed(() => props.block.props.open_label || 'Open');

    // Engine-emitted placement picker (create gate, NEW title). Tree
    // picks land whole ({ value, node_id?, path }); select/text keep a
    // plain string turned into `{ value }` at action time.
    const placementField = computed(() =>
      props.interactive ? props.block.props.placement_field : undefined,
    );
    const placementPick = ref(null);
    const placementValue = ref('');

    /** The `_node_parent` payload the footer action carries — null when
     * nothing was chosen (the create then proceeds unplaced). */
    function currentPlacementPick() {
      if (!placementField.value) return null;
      if (placementField.value.type === 'tree') return placementPick.value;
      const v = placementValue.value.trim();
      return v ? { value: v } : null;
    }

    // Rows render grouped under family headings whenever the engine
    // supplied families (a single family included — the heading and its
    // descriptor orient the user); family-less lists stay flat. The
    // heading is where the main hierarchy lives — rows show status +
    // other-hierarchy context only.
    const displayRows = computed(() => groupDocRowsByFamily(props.block.props.items || []));

    // Engine-flagged compare (duplicate-create gate): needs an in-app
    // compare surface and at least two docs with resolvable ids.
    const comparableDocs = computed(() =>
      (props.block.props.items || []).filter((d) => Number.isFinite(d.doc_id)),
    );
    const canCompare = computed(
      () =>
        props.interactive &&
        props.block.props.compare === true &&
        props.compareAvailable &&
        comparableDocs.value.length >= 2,
    );

    function compareDocs() {
      if (!canCompare.value) return;
      emit('compare-documents', comparableDocs.value.map((d) => ({
        doc_id: d.doc_id,
        reference: d.reference,
        doc_name: d.name,
      })));
    }

    // In-app open needs a resolvable doc_id (the spec coerces with
    // Number(), so a missing id arrives as NaN — hence isFinite).
    function canOpenInApp(doc) {
      return props.interactive && props.openAvailable && Number.isFinite(doc.doc_id);
    }

    // Quick view (the document-summary popover) — same availability
    // gate as the row click's summary path.
    function canQuickView(doc) {
      return props.interactive && props.summaryAvailable && Number.isFinite(doc.doc_id);
    }

    function quickView(event, doc) {
      if (!canQuickView(doc)) return;
      emit('open-summary', event, {
        reference: doc.reference,
        doc_id: doc.doc_id,
        doc_name: doc.name,
      });
    }

    function openDoc(doc) {
      emit('open-document', {
        reference: doc.reference,
        doc_id: doc.doc_id,
        doc_name: doc.name,
      });
    }

    function pick(doc) {
      if (!props.interactive) return;
      emit('action', {
        key: 'selected',
        label: `I chose: ${doc.name} (${doc.reference})`,
        value: doc.reference,
      });
    }

    // "Why it matches" disclosure state, keyed per row. Reassigned (not
    // mutated) so Vue 2.7's reactivity sees new keys.
    const whyOpen = ref({});
    function whyKey(doc) {
      return String(doc.doc_id ?? doc.reference);
    }
    function toggleWhy(doc) {
      const k = whyKey(doc);
      whyOpen.value = { ...whyOpen.value, [k]: !whyOpen.value[k] };
    }
    function isWhyOpen(doc) {
      return !!whyOpen.value[whyKey(doc)];
    }

    function itemClick(doc, event) {
      if (!props.interactive) return;
      if (props.summaryAvailable && doc.doc_id != null) {
        emit('open-summary', event, {
          reference: doc.reference,
          doc_id: doc.doc_id,
          doc_name: doc.name,
        });
        return;
      }
      pick(doc);
    }

    /**
     * Engine-emitted footer/secondary action ("Create anyway",
     * "Exit workflow", "Search again") — emits the ready-made action key +
     * value payload; the renderer composes the [workflow:action]
     * message from it as usual. The PRIMARY action additionally carries
     * the placement pick (when the card asked for one) as
     * `_node_parent`, with the choice reflected in the bubble label.
     */
    function actionClick(fa, primary = false) {
      if (!props.interactive || !fa) return;
      let value = fa.value;
      let label = fa.label;
      if (primary && placementField.value) {
        // ALWAYS carry the key when the card asked: `null` is the
        // user's answer "no family", which the engine must be able to
        // tell apart from never having asked (it warns about the
        // unplaced, content-less result).
        const placement = currentPlacementPick();
        value = { ...(value || {}), _node_parent: placement };
        const where = placement
          ? (Array.isArray(placement.path) ? placement.path.join(' / ') : placement.value)
          : null;
        label = where ? `${fa.label} — under ${where}` : `${fa.label} — no family chosen`;
      }
      // Footer actions are COMMANDS, not answers — the bubble shows the
      // label verbatim.
      emit('action', {
        key: fa.action,
        label,
        value,
      });
    }

    return {
      pick, itemClick, openLabel, displayRows, canOpenInApp, openDoc, actionClick,
      canQuickView, quickView,
      canCompare, comparableDocs, compareDocs,
      // Pill text + the hover reason for its band (shared copy).
      matchLabel: matchTierLabel,
      matchTitle: matchTierTitle,
      // Status-dot colour (shared prototype STATUS_COLOR mapping).
      statusColor: docStatusColor,
      // "Why it matches" disclosure.
      toggleWhy,
      isWhyOpen,
      placementField, placementPick, placementValue,
    };
  },
});
</script>

<style scoped>
/* Prototype Surface treatment (DS MatchesCard): quiet white card —
   hairline grey border, 16px radius, whisper of a shadow. */
.ui-doclist {
  margin-top: 12px;
  padding: 20px;
  border-radius: 16px;
  border: 1px solid rgba(229, 231, 235, 0.9);
  background: white;
  box-shadow: 0 1px 2px rgba(16, 24, 40, 0.04);
}
.ui-doclist__prompt-row {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 8px;
}
/* Card heading (DS Eyebrow): small, semibold, quiet grey. */
.ui-doclist__prompt {
  flex: 1 1 auto;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: -0.01em;
  color: rgb(156 163 175);
}
/* Quiet indigo text action (DS "Use this" affordance). */
.ui-doclist__compare {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  flex: 0 0 auto;
  padding: 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 500;
  color: rgb(79 70 229);
  cursor: pointer;
  transition: color 0.15s ease;
}
.ui-doclist__compare:hover {
  color: rgb(67 56 202);
}
.ui-doclist__empty,
.ui-doclist__inert {
  font-size: 12px;
  color: rgb(107 114 128);
}
/* Results as a divided list (DS MatchItem rows), not bordered boxes —
   the card supplies the containment. */
.ui-doclist__results {
  display: flex;
  flex-direction: column;
}
.ui-doclist__result {
  display: flex;
  /* Top-aligned: the match pill + Open sit level with the title line
     even when the reasons list expands the row. */
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
  width: 100%;
  box-sizing: border-box;
  padding: 12px 6px;
  border-radius: 8px;
  border-bottom: 1px solid rgb(243 244 246);
  background: transparent;
  cursor: pointer;
  text-align: left;
  font-family: inherit;
  transition: background 0.12s ease;
}
/* Trailing column: match pill on top, "Why it matches" beneath it,
   right-aligned and level with the title line. */
.ui-doclist__trail {
  flex: 0 0 auto;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 6px;
  margin-top: 2px;
}
.ui-doclist__result:last-child {
  border-bottom: 0;
}
.ui-doclist__result:hover {
  background: rgb(249 250 251);
}
.ui-doclist__result-text {
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
  /* Take the slack so the match pill + Open sit together on the right. */
  flex: 1 1 auto;
}
/* Shared between the in-app <button> and the deep-link <a> fallback —
   quiet indigo text action (DS "Use this" affordance). */
.ui-doclist__open {
  flex: 0 0 auto;
  padding: 0;
  border: 0;
  background: none;
  font-size: 12.5px;
  font-weight: 500;
  font-family: inherit;
  color: rgb(79 70 229);
  text-decoration: none;
  white-space: nowrap;
  cursor: pointer;
  transition: color 0.15s ease;
}
.ui-doclist__open:hover {
  color: rgb(67 56 202);
  background: none;
  text-decoration: underline;
}
/* Quiet grey "View" — opens the quick-view popover (prototype's View
   action: grey at rest, darkens on hover). */
.ui-doclist__view {
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 0;
  border: 0;
  background: none;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 500;
  color: rgb(156 163 175);
  cursor: pointer;
  transition: color 0.15s ease;
}
.ui-doclist__view:hover {
  color: rgb(55 65 81);
  text-decoration: underline;
}
.ui-doclist__view .v-icon {
  color: currentColor !important;
}
/* A touch more separation between the status and the actions. */
.ui-doclist__sub .ui-doclist__open {
  margin-left: 6px;
}
.ui-doclist__placement {
  margin-top: 12px;
}
/* Consequence line under the picker — the prototype's quiet amber
   note: soft wash with an amber left tick, readable grey text. */
.ui-doclist__placement-notice {
  margin-top: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  border-left: 2px solid rgb(245 158 11);
  background: rgb(255 251 235);
  font-size: 11.5px;
  line-height: 1.45;
  color: rgb(75 85 99);
}
/* Standing alone it states where the profile WILL go: information. */
.ui-doclist__placement-notice--info {
  margin-top: 0;
  padding: 0;
  border-left: 0;
  background: none;
  color: rgb(71 85 105);
}
/* Footer (DS MatchesCard): hairline-separated, primary + quiet way-out. */
.ui-doclist__footer {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
  padding-top: 14px;
  border-top: 1px solid rgb(243 244 246);
}
.ui-doclist__footer-action {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  height: 36px;
  padding: 0 16px;
  border: 0;
  border-radius: 8px;
  background: rgb(79 70 229);
  color: white;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: background 0.15s ease;
}
.ui-doclist__footer-action:hover {
  background: rgb(67 56 202);
}
/* Quiet way-out (DS QuietBtn): bare text that darkens on hover. */
.ui-doclist__secondary-action {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: 0;
  background: none;
  color: rgb(107 114 128);
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  font-family: inherit;
  transition: color 0.15s ease;
}
.ui-doclist__secondary-action:hover {
  border-color: transparent;
  color: rgb(17 24 39);
}
/* Top line: title + other-hierarchy context. */
.ui-doclist__name {
  display: flex;
  align-items: baseline;
  gap: 8px;
  min-width: 0;
}
.ui-doclist__name-text {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 15px;
  font-weight: 600;
  color: rgb(17 24 39);
}
.ui-doclist__ctx {
  flex: 0 1 auto;
  min-width: 0;
  font-size: 12px;
  color: rgb(156 163 175);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* Second line: status dot + status. */
.ui-doclist__sub {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  font-size: 12px;
  color: rgb(107 114 128);
}
.ui-doclist__dot {
  flex: 0 0 auto;
  width: 6px;
  height: 6px;
  border-radius: 9999px;
}
</style>

<!-- Shared match-badge look — edit uiBlockShared.css, not the scoped block above. -->
<style src="./uiBlockShared.css"></style>
